import { useContext, useEffect, useRef, useState } from 'react';
import { shallowEqual, useDispatch } from 'react-redux';
import { cloneDeep } from 'es-toolkit';

import {
  AdornedInput,
  Alert,
  Anchor,
  FormField,
  FormHelp,
  Heading,
  Label,
  Paragraph,
  Popover,
  Text,
  TextButton,
  TriggerText,
} from '@zillow/constellation-10';
import { useQueryClient } from '@zg-rentals/react-query5';

import { GET_LISTING_RECS } from '../../../../constants/RecommendationsConstants';
import WizardServerError from '../../../../model/WizardServerError';
import SingleFamilyCSEvents from '../../../../model/ClickstreamEvent/TotalMonthlyPriceWithFee/SingleFamilyCSEvents';
import numberUtils from '../../../../utils/numberUtils';
import accessUtils from '../../../../utils/accessUtils';
import { ModularListingContext } from '../../../../containers/CreateListingPage/context';
import { WizardContext } from '../../../../components/Wizard/context';
import { DISPLAY_SERVER_ERROR_AT } from '../../../../constants/WizardConstants';
import actions from '../../../../containers/CreateListingPage/actions';
import wizardActions from '../../../../components/Wizard/actions';
import { AppSelectors } from '../../../../selectors';
import { useSelector } from '../../../../selectors/useSelector';
import { useForm } from '../../../../hooks';

import { postingConstraints } from '../../../../utils/formValidation';
import PostingActions from '../../../../actions/PostingActions';
import PropertiesActions from '../../../../actions/PropertiesActions';
import WizardAlert from '../../../../components/WizardAlert';
import InputFieldWarningConfirmationSFModal from '../../../../components/Modal/InputFieldWarningConfirmationSFModal';

import type { ChangeEvent } from 'react';

const inputName = 'price';

const priceWarningLimit = 50000;

const rentZestimateLink = 'https://www.zillow.com/z/rental/what-is-a-rent-zestimate/';

const mockEvent = {
  target: {
    name: inputName,
    value: null,
  },
};

type Props = {
  isRelistingFieldset?: boolean;
};

const MonthlyRentFormField = ({ isRelistingFieldset = false }: Props) => {
  const dispatch = useDispatch();
  const queryClient = useQueryClient();

  const { reducerWizardDispatch } = useContext(WizardContext);
  const {
    modularListingState: { serverErrorObj, shouldSaveOnBlur, isInModalState, isSaveComplete },
    reducerDispatch,
  } = useContext(ModularListingContext);

  const isScreenDesktop = useSelector((state) => AppSelectors.makeIsScreenDesktop(state));
  const {
    appConfigCanAccessTotalMonthlyPriceWithFee,
    cookieEnabledServices,
    currentProperty,
    floorplans,
    isTotalMonthlyPriceWithFeeEligible,
    modularListingProgressStep,
    rentZestimate,
    user,
  } = useSelector(
    (state) => ({
      appConfigCanAccessTotalMonthlyPriceWithFee: state.app.appConfig.config.features.canAccessTotalMonthlyPriceWithFee,
      cookieEnabledServices: state.app.cookieEnabledServices,
      currentProperty: state?.properties?.currentProperty,
      floorplans: state.properties.currentProperty.floorplans,
      isTotalMonthlyPriceWithFeeEligible: state.properties.currentProperty?.totalMonthlyPriceWithFeeNotice?.isEligible,
      modularListingProgressStep: state.properties?.currentProperty?.modularListingProgress?.step,
      rentZestimate: state.properties.currentProperty.rentZestimate,
      user: state?.user,
    }),
    shallowEqual,
  );

  const previousValue = useRef();
  const priceInputRef = useRef<HTMLInputElement>(null);

  const [showWarningConfirmationModal, setShowWarningConfirmationModal] = useState(false);

  const floorplan = floorplans[0] || {};
  const { price, floorPlanId } = floorplan;

  const {
    inputs,
    errorObj,
    hasFieldError: hasFieldErrorCheck,
    handleChange: handleFormChange,
  } = useForm(
    {
      price,
    },
    postingConstraints,
  );

  const canAccessTotalMonthlyPriceWithFee = accessUtils.canAccessTotalMonthlyPriceWithFee({
    cookieEnabledServices,
    canAccessTotalMonthlyPriceWithFee: appConfigCanAccessTotalMonthlyPriceWithFee,
    isEligible: isTotalMonthlyPriceWithFeeEligible,
  });

  useEffect(() => {
    // @ts-expect-error
    previousValue.current = inputs[inputName];
  }, []);

  useEffect(() => {
    if (isSaveComplete) {
      save({
        target: {
          name: inputName,
          // @ts-expect-error
          value: inputs[inputName],
        },
      });
    }
  }, [isSaveComplete]);

  const clearServerError = () => {
    if (serverErrorObj.fieldName) {
      actions.removeServerError({
        reducerDispatch,
      });
    }
  };

  const save = (event: ChangeEvent<HTMLInputElement>) => {
    const { value, name: fieldName } = event.target;

    // field error check needs to be above must click on cta check
    if (hasFieldErrorCheck(fieldName, value)) {
      actions.updateHasFieldError({ reducerDispatch, hasFieldError: true });
      actions.updateIsSaveComplete({ reducerDispatch, isSaveComplete: false });

      return;
    }

    actions.updateHasFieldError({ reducerDispatch, hasFieldError: false });

    const numCastValue = Number(value || 0);
    const numCastPrevValue = Number(previousValue.current || 0);

    if (numCastValue === numCastPrevValue && !isInModalState) return;

    if (numCastValue !== numCastPrevValue && numCastValue >= priceWarningLimit && !showWarningConfirmationModal) {
      // open confirmation modal
      setShowWarningConfirmationModal(true);
      return;
    }

    // @ts-expect-error
    previousValue.current = inputs[inputName];

    // return if user must click on cta
    if (!shouldSaveOnBlur && !isSaveComplete) {
      return;
    }

    const dataToSave = {
      fieldName,
      value,
      floorPlanId,
    };

    wizardActions.updateIsFetchingData({ reducerWizardDispatch, isFetchingData: true });

    dispatch(PostingActions.floorplan.edit(dataToSave))
      // @ts-expect-error
      .then(() => {
        const copyFloorplan = cloneDeep(floorplans);
        copyFloorplan[0][fieldName] = Number(value);
        queryClient.invalidateQueries({ queryKey: [GET_LISTING_RECS] });

        dispatch(PropertiesActions.updateCurrentProperty({ floorplans: copyFloorplan }));
        dispatch(PropertiesActions.updateCurrentProperty({ price: Number(value) }));

        // reset save cta
        actions.updateIsSaveComplete({ reducerDispatch, isSaveComplete: false });
      })
      .catch((message: string) => {
        updateServerError(message, fieldName);
      })
      .finally(() => {
        wizardActions.updateIsFetchingData({
          reducerWizardDispatch,
          isFetchingData: false,
        });
      });
  };

  const updateServerError = (message = '', fieldName = '') => {
    const displayAt = isInModalState ? DISPLAY_SERVER_ERROR_AT.FIELD : DISPLAY_SERVER_ERROR_AT.PAGE;

    actions.updateServerError({
      reducerDispatch,
      serverErrorObj: WizardServerError({
        displayAt,
        fieldName,
        message,
        displayMessage: message,
      }),
    });
  };

  const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
    clearServerError();
    // @ts-expect-error
    handleFormChange(event);
  };

  // confirmation modal related functions
  const handleModalClose = () => {
    setShowWarningConfirmationModal(false);
    // @ts-expect-error
    mockEvent.target.value = previousValue.current;

    handleFormChange(mockEvent);
    // @ts-expect-error
    priceInputRef.current.focus();
  };

  const handleModalSubmit = () => {
    if (isInModalState) {
      actions.updateIsSaveComplete({ reducerDispatch, isSaveComplete: true });

      return;
    }

    // @ts-expect-error
    mockEvent.target.value = inputs[inputName];
    // @ts-expect-error
    save(mockEvent);

    setShowWarningConfirmationModal(false);
  };

  const renderZestimate = () => {
    const rentZestimateCurrency = numberUtils.asCurrency(Number(rentZestimate));
    return (
      <Popover
        body={
          <Text>
            The Rent Zestimate is Zillow&apos;s best estimate of a home&apos;s value as a rental property.{' '}
            <TextButton asChild>
              <a href={rentZestimateLink} rel="noreferrer" target="_blank">
                Learn more
              </a>
            </TextButton>
            {' about the Rent Zestimate.'}
          </Text>
        }
        header={<Heading level={4}>Rent Zestimate®</Heading>}
        trigger={<TriggerText css={{ fontWeight: 'regular' }}>Rent Zestimate®: {rentZestimateCurrency}</TriggerText>}
      />
    );
  };

  return (
    <>
      <WizardAlert serverErrorObj={serverErrorObj} displayAt={DISPLAY_SERVER_ERROR_AT.FIELD} />

      {!!rentZestimate && !isRelistingFieldset && (
        <Paragraph
          css={{ marginBottom: '600' }}
        >{`The current Rent Zestimate for this property is: ${numberUtils.asCurrency(rentZestimate)}`}</Paragraph>
      )}
      <FormField
        data-testid="MonthlyRentFormField"
        label={
          <Label required={true}>
            {canAccessTotalMonthlyPriceWithFee ? 'Base rent' : 'Monthly rent'}&nbsp;
            {isRelistingFieldset && !!rentZestimate && renderZestimate()}
          </Label>
        }
        control={
          <AdornedInput
            size={isScreenDesktop ? 'lg' : 'md'}
            input={
              <AdornedInput.Input
                ref={priceInputRef}
                name={inputName}
                value={String(inputs[inputName] || '')}
                onChange={handleChange}
                onBlur={save}
              />
            }
            startAdornment={<AdornedInput.Adornment>$</AdornedInput.Adornment>}
            endAdornment={<AdornedInput.Adornment>/month</AdornedInput.Adornment>}
          />
        }
        // @ts-expect-error
        error={!!errorObj.price}
        // @ts-expect-error
        description={<FormHelp error={!!errorObj.price}>{errorObj.price}</FormHelp>}
      />

      {canAccessTotalMonthlyPriceWithFee && (
        <Alert role="alert">
          You&apos;ll have the opportunity later to itemize any fees for this listing.{' '}
          <Anchor
            target="_blank"
            href="https://www.zillow.com/rentals-network/pricing-transparency/"
            onClick={() => {
              SingleFamilyCSEvents.LearnMore({
                user,
                currentProperty,
                triggerObject: 'monthly_rent_and_security_deposit',
                triggerLocation: !modularListingProgressStep?.includes('review')
                  ? 'posting_path'
                  : 'posting_path_review',
              });
            }}
            data-eventid={SingleFamilyCSEvents.LearnMore.data_event_id}
          >
            Learn more
          </Anchor>{' '}
          about fee transparency regulations in your area.
        </Alert>
      )}

      {showWarningConfirmationModal && (
        <InputFieldWarningConfirmationSFModal
          fieldName={inputName}
          // @ts-expect-error
          previousValue={previousValue.current}
          // @ts-expect-error
          value={inputs[inputName]}
          isModularListing={true}
          handleModalCloseML={handleModalClose}
          handleModalSubmitML={handleModalSubmit}
        />
      )}
    </>
  );
};

export default MonthlyRentFormField;
